import {
  FIELD_LABELS,
  FIELD_OPS,
  NUMERIC_FIELDS,
  type Rule,
  type RuleField,
  type RuleGroup,
  type RuleNode,
  type RuleOp,
  joinWord,
  nestedJoinWord,
  validate,
} from "@trackmark/core";
import styles from "./RuleBuilder.module.css";

/**
 * The rule builder (design/README.md §10).
 *
 * *"Rules are the content; the chrome nearly disappears."* So a group is a `border-left` and an
 * indent, not a box — nesting reads as an outline rather than as a stack of cards. The operator
 * words carry the structure, which is why they are the only coloured thing in a row.
 *
 * Shared with the playlist generator wholesale, per §J15: a playlist is a list plus a way of
 * choosing from it, and two rule builders would be two rule languages within a month.
 */

const OP_LABELS: Record<RuleOp, string> = {
  is: "is",
  "is-not": "is not",
  "at-least": "at least",
  "at-most": "at most",
  contains: "contains",
  before: "before",
  after: "after",
};

const FIELDS = Object.keys(FIELD_LABELS) as RuleField[];

/** Fields whose value is a yes/no rather than something to type. */
const BOOLEAN_FIELDS = new Set<RuleField>(["favourite", "rated"]);

export interface RuleBuilderProps {
  value: RuleGroup;
  onChange: (next: RuleGroup) => void;
  /** Marginal cost per rule, in the same depth-first order as `flattenRules`. */
  hints?: { excludes: number }[];
}

export function RuleBuilder({ value, onChange, hints = [] }: RuleBuilderProps) {
  const problems = validate(value);
  // A running index so each rule row can find its hint without the tree carrying positions.
  let cursor = 0;
  const nextHintIndex = () => cursor++;

  /**
   * Appends into the group at `path`. Separate from `replaceAt` on purpose: an index one past the
   * end never matches a child, so reusing the replace walk to add would silently do nothing.
   */
  const appendAt = (group: RuleGroup, path: number[], node: RuleNode): RuleGroup => {
    if (path.length === 0) return { ...group, children: [...group.children, node] };
    const [head, ...rest] = path;
    return {
      ...group,
      children: group.children.map((child, i) =>
        i === head && child.kind === "group" ? appendAt(child, rest, node) : child,
      ),
    };
  };

  const replaceAt = (group: RuleGroup, path: number[], next: RuleNode | null): RuleGroup => {
    const [head, ...rest] = path;
    const children = group.children.flatMap((child, i) => {
      if (i !== head) return [child];
      if (rest.length === 0) return next === null ? [] : [next];
      return [replaceAt(child as RuleGroup, rest, next)];
    });
    return { ...group, children };
  };

  const renderGroup = (group: RuleGroup, path: number[], depth: number) => (
    <div className={styles.group} data-depth={depth} data-testid="rule-group">
      {group.children.map((child, index) => {
        const here = [...path, index];
        const word = joinWord(group, index);

        if (child.kind === "group") {
          // The NOT belongs to this child, not to the group it sits in.
          const head = nestedJoinWord(group, index, child);
          return (
            <div key={here.join("-")} className={styles.nested}>
              <div className={styles.groupHead}>
                <span className={styles.op} data-strong={head.includes("NOT") || undefined}>
                  {head}
                </span>
                <button
                  type="button"
                  className={styles.joinToggle}
                  data-testid="group-join"
                  onClick={() =>
                    onChange(replaceAt(value, here, { ...child, join: child.join === "and" ? "or" : "and" }))
                  }
                >
                  {child.join === "and" ? "ALL OF" : "ANY OF"}
                </button>
                <button
                  type="button"
                  className={styles.remove}
                  aria-label="Remove group"
                  onClick={() => onChange(replaceAt(value, here, null))}
                >
                  ×
                </button>
              </div>
              {renderGroup(child, here, depth + 1)}
            </div>
          );
        }

        const hintIndex = nextHintIndex();
        const hint = hints[hintIndex];
        const problem = problems.find((p) => p.path.join("-") === here.join("-"));

        return (
          <div key={here.join("-")} className={styles.row} data-testid="rule-row" data-problem={problem ? "true" : undefined}>
            <span className={styles.op} data-strong={word.includes("NOT") || undefined}>
              {word}
            </span>

            <select
              className={styles.select}
              aria-label="Field"
              value={child.field}
              onChange={(event) => {
                const field = event.target.value as RuleField;
                // The operator has to stay legal for the new field, or the row becomes a rule
                // nobody can express and the count stops moving with no visible reason.
                const op = FIELD_OPS[field].includes(child.op) ? child.op : FIELD_OPS[field][0]!;
                onChange(replaceAt(value, here, { ...child, field, op, value: BOOLEAN_FIELDS.has(field) ? true : null }));
              }}
            >
              {FIELDS.map((f) => (
                <option key={f} value={f}>
                  {FIELD_LABELS[f]}
                </option>
              ))}
            </select>

            <select
              className={styles.select}
              aria-label="Operator"
              value={child.op}
              onChange={(event) => onChange(replaceAt(value, here, { ...child, op: event.target.value as RuleOp }))}
            >
              {FIELD_OPS[child.field].map((op) => (
                <option key={op} value={op}>
                  {OP_LABELS[op]}
                </option>
              ))}
            </select>

            {BOOLEAN_FIELDS.has(child.field) ? (
              <select
                className={styles.select}
                aria-label="Value"
                value={child.value === true || child.value === "true" ? "true" : "false"}
                onChange={(event) => onChange(replaceAt(value, here, { ...child, value: event.target.value === "true" }))}
              >
                <option value="true">yes</option>
                <option value="false">no</option>
              </select>
            ) : (
              <input
                className={styles.input}
                aria-label="Value"
                data-testid="rule-value"
                value={child.value === null ? "" : String(child.value)}
                onChange={(event) => {
                  const raw = event.target.value;
                  const numeric = NUMERIC_FIELDS.includes(child.field);
                  onChange(replaceAt(value, here, { ...child, value: raw === "" ? null : numeric ? Number(raw) : raw }));
                }}
              />
            )}

            {/* The marginal cost of this row, which is what makes a tree debuggable: `excludes
                118` says what this rule is actually doing to the count. */}
            <span className={styles.hint} data-testid="rule-hint">
              {problem ? problem.message : hint === undefined ? "" : hint.excludes === 0 ? "excludes nothing" : `excludes ${hint.excludes.toLocaleString()}`}
            </span>

            <button
              type="button"
              className={styles.remove}
              aria-label={`Remove rule ${hintIndex + 1}`}
              onClick={() => onChange(replaceAt(value, here, null))}
            >
              ×
            </button>
          </div>
        );
      })}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.add}
          data-testid="add-rule"
          onClick={() =>
            onChange(appendAt(value, path, { kind: "rule", field: "score", op: "at-least", value: null } as Rule))
          }
        >
          + rule
        </button>
        <button
          type="button"
          className={styles.add}
          data-testid="add-group"
          onClick={() => onChange(appendAt(value, path, { kind: "group", join: "or", children: [] }))}
        >
          + group
        </button>
        <button
          type="button"
          className={styles.add}
          data-testid="add-not-group"
          onClick={() => onChange(appendAt(value, path, { kind: "group", join: "and", negate: true, children: [] }))}
        >
          + NOT group
        </button>
      </div>
    </div>
  );

  return <div className={styles.builder}>{renderGroup(value, [], 0)}</div>;
}
